import React, { useEffect, useMemo, useState } from "react";

import TeamMembersSection from "./TeamMembersSection";
import KpiCard from "../../Component/EmployeeComponents/KpiCard";
import LeadsTable from "./LeadsTable";
import CreateLeadForm from "./CreateLeadForm";
import TargetCard from "../../Component/EmployeeComponents/TargetCard";
import MonthlyWonChart from "./MonthlyWonChart";
import LeadsByStatusBar from "../../Component/EmployeeComponents/LeadsByStatusBar";
import ReportsTable from "./ReportsTable";
import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
import IncentiveCard from "../../Component/EmployeeComponents/IncentiveCard";
import AccessBlockedModal from "../Main/Admin/AccessBlockedModal";
import { BASE_URL } from "../../config/api";
import { ValueLoader } from "../../Component/Common/ValueLoader";
import { BarChart3, Percent, TrendingUp, Trophy } from "lucide-react";
import axios from "axios";

export default function EmployeeDashboard({ employeeId }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [leadTab, setLeadTab] = useState("my"); // my | junior
  const [filterStatus, setFilterStatus] = useState("All");
  const [summary, setSummary] = useState(null);
  const [locationType, setLocationType] = useState(""); // state | district | pincode
  const [locationValue, setLocationValue] = useState("");

  const [pagination, setPagination] = useState({
  current_page: 1,
  last_page: 1,
});
// const [followupcounts, setfollowupcounts] = useState({})

const [todayFollowup, setTodayFollowup] = useState(false);
const [tomorrowFollowup, setTomorrowFollowup] = useState(false);
const [pendingFollowup, setPendingFollowup] = useState(false);
const [upcomingFollowup, setUpcomingFollowup] = useState(false);

const [dateFilterType, setDateFilterType] = useState("lead");

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const userRole = Number(authUser?.role);
  const companyId = authUser?.company_id;
  const userId = employeeId || authUser?.id;

  /* ================= FETCH LEADS ================= */
  const fetchLeads = async (page = 1) => {
    setLoading(true);

    setError(null);

    try {

      const params = new URLSearchParams();

      params.append("page", page);

      /* ===== ROLE LOGIC ===== */

      if (userRole === 1) {
        if (leadTab === "my") {
          params.append(
            "created_by_id",
            userId
          );
        }
      }

      if (userRole === 2) {

        if (leadTab === "my") {

          params.append(
            "created_by_id",
            userId
          );

        } else {

          params.append(
            "company_id",
            userId
          );

        }
      }

      if (userRole === 3) {

        params.append(
          "company_id",
          companyId
        );

        if (
          authUser?.position ===
          "MANAGER"
        ) {

          if (leadTab === "my") {

            params.append(
              "created_by_id",
              userId
            );

          } else {

            params.append(
              "manager_id",
              authUser?.id
            );

          }
        }

        if (
          authUser?.position ===
          "EXECUTIVE"
        ) {

          params.append(
            "created_by_id",
            userId
          );

        }
      }

      if (userRole === 4) {

        params.append(
          "company_id",
          companyId
        );

        params.append(
          "created_by_id",
          userId
        );
      }

      /* ===== DATE FILTERS ===== */

    if (fromDate instanceof Date) {

      const formatted =
        fromDate
          .toISOString()
          .split("T")[0];

      if (dateFilterType === "lead") {

        params.append(
          "from_date",
          formatted
        );

      } else {

        params.append(
          "followup_from_date",
          formatted
        );

      }
    }

    if (toDate instanceof Date) {

      const formatted =
        toDate
          .toISOString()
          .split("T")[0];

      if (dateFilterType === "lead") {

        params.append(
          "to_date",
          formatted
        );

      } else {

        params.append(
          "followup_to_date",
          formatted
        );

      }
    }

      /* ===== STATUS FILTER ===== */

      if (
        filterStatus &&
        filterStatus !== "All"
      ) {

        params.append(
          "status",
          filterStatus
        );

      }

      /* ===== LOCATION FILTER ===== */

      if (
        locationType &&
        locationValue
      ) {

        if (locationType === "state") {
          params.append(
            "state",
            locationValue
          );
        }

        if (
          locationType === "district"
        ) {
          params.append(
            "district",
            locationValue
          );
        }

        if (
          locationType === "pincode"
        ) {
          params.append(
            "pincode",
            locationValue
          );
        }
      }

      /* ===== FOLLOWUP FILTERS ===== */

      if (todayFollowup) {
        params.append(
          "today_followup",
          1
        );
      }

      if (tomorrowFollowup) {
        params.append(
          "tomorrow_followup",
          1
        );
      }

      // if (tomorrowFollowup) {
      //   params.tomorrow_followup = 1;
      // }

      if (pendingFollowup) {
        params.append(
          "pending_followup",
          1
        );
      }

      if (upcomingFollowup) {
        params.append(
          "upcoming_followup",
          1
        );
      }

      // ✅ API CALL
      const response = await axios.get(
        `${BASE_URL}warranty/wlead/list?${params.toString()}`
      );

      const data = response.data;

      if (!data?.status) {

        throw new Error(
          data?.message ||
          "Failed to fetch leads"
        );

      }

      setSummary(data.summary);

      setPagination(data.pagination);
      
      setLeads(
        data.data.map((lead) => ({
          id: lead.id,

          name: lead.name,

          owner:
            lead.owner_name || "-",

          location:
            lead.district ||
            lead.state ||
            "-",

          type: lead.lead_type,

          status: lead.status,

          value:
            lead.lead_amount || 0,

          createdAt:
            lead.created_at,

          lead_code:
            lead.lead_code,

          created_by_name:
            lead.created_by_name,

          remarks:
          lead.remarks || [],

          full: lead,
        }))
      );

    } catch (err) {

      setError(
        err?.response?.data?.message ||
        err?.message
      );

    } finally {

      setLoading(false);

    }
  };

  /* 🔁 refetch when tab changes */
  useEffect(() => {
    fetchLeads();
  }, [ leadTab, fromDate, toDate, filterStatus, locationType, locationValue, todayFollowup,tomorrowFollowup, pendingFollowup, upcomingFollowup, ]);

  /* ================= METRICS ================= */
  const metrics = useMemo(() => {
    const totalLeads = leads.length;
    const wonLeads = leads.filter((l) => l.status === "Won");
    const lostLeads = leads.filter((l) => l.status === "Lost");
    const inProgressLeads = leads.filter((l) => l.status === "In Progress");
    const newLeads = leads.filter((l) => l.status === "New");

    return {
      totalLeads,
      newLeads: newLeads.length,
      inProgressLeads: inProgressLeads.length,
      wonLeads: wonLeads.length,
      lostLeads: lostLeads.length,
      revenue: wonLeads.reduce((s, l) => s + l.value, 0),
      pipeline: inProgressLeads.reduce((s, l) => s + l.value, 0),
      conversionRate:
        totalLeads === 0 ? 0 : Math.round((wonLeads.length / totalLeads) * 100),
      monthlyTarget: 200000,
    };
  }, [leads]);

  const canViewTeamMembers =
    userRole === 2 || (userRole === 3 && authUser?.position === "MANAGER");

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">
        <div className="mx-auto px-1 py-2 space-y-3">

          {/* ===== KPIs ===== */}
         <section className="grid grid-cols-2 lg:grid-cols-4  gap-4">
            <KpiCard
              label="Total Leads"
              value={loading ? <ValueLoader /> : summary?.total_leads ?? 0}
              icon={BarChart3}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
            />

            <KpiCard
              label="Won Deals"
              value={loading ? <ValueLoader /> : summary?.status_counts?.won ?? 0}
              sublabel={`₹${summary?.status_amounts?.won ?? 0}`}
              icon={Trophy}
              iconBg="bg-green-50"
              iconColor="text-green-600"
            />

            <KpiCard
              label="Pipeline Value"
              value={
                loading ? (
                  <ValueLoader />
                ) : (
                  <>₹{summary?.status_amounts?.in_process ?? 0}</>
                )
              }
              sublabel={
                loading
                  ? <ValueLoader />
                  : `${summary?.status_counts?.in_process ?? 0} in progress`
              }
              icon={TrendingUp}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
            />

            <KpiCard
              label="Conversion Rate"
              value={loading ? <ValueLoader /> : summary?.conversion_rate ?? "0%"}
              icon={Percent}
              iconBg="bg-purple-50"
              iconColor="text-purple-600"
            />

          </section>

          {/* ===== TABLE + SIDE ===== */}
          <section className="grid grid-cols-1 xl:grid-cols-2 gap-5 space-y-3">
            <div className="xl:col-span-4">
              <LeadsTable
                leads={leads}
                pagination={pagination}
                summary={summary}
                setLeads={setLeads}
                loading={loading}
                leadTab={leadTab}
                setLeadTab={setLeadTab}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                onRefresh={fetchLeads}
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                locationType={locationType}
                setLocationType={setLocationType}
                locationValue={locationValue}
                setLocationValue={setLocationValue}
                canViewTeamMembers={canViewTeamMembers}
                todayFollowup={todayFollowup}
                setTodayFollowup={setTodayFollowup}

                tomorrowFollowup={tomorrowFollowup}
                setTomorrowFollowup={setTomorrowFollowup}

                pendingFollowup={pendingFollowup}
                setPendingFollowup={setPendingFollowup}

                upcomingFollowup={upcomingFollowup}
                setUpcomingFollowup={setUpcomingFollowup}

                dateFilterType={dateFilterType}
                setDateFilterType={setDateFilterType}
              />
            </div>
          </section>
            <div className="space-y-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* <CreateLeadForm onCreate={fetchLeads} /> */}
              {/* <TargetCard target={metrics.monthlyTarget} achieved={metrics.revenue} />
              <IncentiveCard targetIncentive={50000} achievedIncentive={32000} /> */}
              <div>
                <ReportsTable leads={leads} />
                </div> 

              <div className="space-y-2">
                <LeadsByStatusBar leads={leads} />
              {/* <MonthlyWonChart leads={leads} /> */}
               {canViewTeamMembers && <TeamMembersSection />}
              </div>
            </div>

          {/* ===== CHARTS ===== */}
          {/* <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
          </section> */}
        </div>
      </main> 

      <AccessBlockedModal user={authUser} allowedRoles={[2]} />
    </div>
  );
}



// import React from "react";

// import TeamMembersSection from "./TeamMembersSection";
// import KpiCard from "../../Component/EmployeeComponents/KpiCard";
// import LeadsTable from "./LeadsTable";
// import CreateLeadForm from "./CreateLeadForm";
// import TargetCard from "../../Component/EmployeeComponents/TargetCard";
// import MonthlyWonChart from "./MonthlyWonChart";
// import LeadsByStatusBar from "../../Component/EmployeeComponents/LeadsByStatusBar";
// import ReportsTable from "./ReportsTable";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import IncentiveCard from "../../Component/EmployeeComponents/IncentiveCard";
// import AccessBlockedModal from "../Main/Admin/AccessBlockedModal";

// const initialLeads = [
//   {
//     id: 1,
//     name: "Acme Corp Website Revamp",
//     owner: "John Doe",
//     status: "New",
//     location:"vashi",
//     value: 12000,
//     createdAt: "2025-01-15",
//   },
//   {
//     id: 2,
//     name: "Globex Cloud Migration",
//     owner: "Sarah Lee",
//     status: "In Progress",
//     location:"Chembur",
//     value: 45000,
//     createdAt: "2025-02-10",
//   },
//   {
//     id: 3,
//     name: "Initech Support Contract",
//     owner: "Michael Scott",
//     status: "Won",
//     location:"vashi",
//     value: 30000,
//     createdAt: "2025-02-25",
//   },
//   {
//     id: 4,
//     name: "Umbrella Security Audit",
//     owner: "Angela Martin",
//     status: "Lost",
//     location:"KoparKhairane",
//     value: 18000,
//     createdAt: "2025-03-05",
//   },
//   {
//     id: 5,
//     name: "Stark Industries License",
//     owner: "Tony Stark",
//     status: "Won",
//     location:"vashi",
//     value: 80000,
//     createdAt: "2025-03-22",
//   },
//   {
//     id: 6,
//     name: "Wayne Enterprises CRM",
//     owner: "Bruce Wayne",
//     status: "In Progress",
//     location:"Ghansoli",
//     value: 52000,
//     createdAt: "2025-04-02",
//   },
//   {
//     id: 7,
//     name: "Hooli Cloud Expansion",
//     owner: "Pied Piper",
//     status: "New",
//     location:"Rabale",
//     value: 26000,
//     createdAt: "2025-04-18",
//   },
//   {
//     id: 8,
//     name: "Aperture Science SLA",
//     owner: "Chell",
//     status: "Won",
//     location:"Thane",
//     value: 15000,
//     createdAt: "2025-05-03",
//   },
// ];

// export default function EmployeeDashboard() {
//   const [leads, setLeads] = React.useState(initialLeads);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   const userRole = Number(authUser?.role); // 1 = admin, 2 = company
//   const companyId = authUser?.id; 
//   console.log("leads data", leads)
//   const [filterStatus, setFilterStatus] = React.useState("All");



//   const filteredLeads = React.useMemo(() => {
//     if (filterStatus === "All") return leads;
//     return leads.filter((l) => l.status === filterStatus);
//   }, [leads, filterStatus]);

//   const metrics = React.useMemo(() => {
//     const totalLeads = leads.length;
//     const wonLeads = leads.filter((l) => l.status === "Won");
//     const lostLeads = leads.filter((l) => l.status === "Lost");
//     const inProgressLeads = leads.filter((l) => l.status === "In Progress");
//     const newLeads = leads.filter((l) => l.status === "New");

//     const revenue = wonLeads.reduce((sum, l) => sum + l.value, 0);
//     const pipeline = inProgressLeads.reduce((sum, l) => sum + l.value, 0);
//     const conversionRate =
//       totalLeads === 0 ? 0 : Math.round((wonLeads.length / totalLeads) * 100);

//     const monthlyTarget = 200000;

//     return {
//       totalLeads,
//       newLeads: newLeads.length,
//       inProgressLeads: inProgressLeads.length,
//       wonLeads: wonLeads.length,
//       lostLeads: lostLeads.length,
//       revenue,
//       pipeline,
//       conversionRate,
//       monthlyTarget,
//     };
//   }, [leads]);

//   const handleCreateLead = (lead) => {
//     setLeads((prev) => [lead, ...prev]);
//   };

//   const canViewTeamMembers =
//   userRole === 2 ||
//   (userRole === 3 && authUser?.position === "manager");


//   return (
//     <div className="min-h-screen flex flex-col">
//       {/* <header className="bg-white border-b border-slate-200">
//         <div className=" mx-auto px-4 py-3 flex items-center justify-between gap-3">
//           <div className="flex items-center gap-2">
//             <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold">
//               EX
//             </div>
//             <div>
//               <div className="text-sm md:text-base font-semibold text-slate-900">
//                 Executive Leads Dashboard
//               </div>
//               <div className="text-[11px] text-slate-500">
//                 Blue &amp; white executive view of leads, KPIs, reports &amp; team
//               </div>
//             </div>
//           </div>
//         </div>
//       </header> */}

//       <main className="flex-1">
//         <div className=" mx-auto px-4 py-4 md:py-6 space-y-4 md:space-y-6">
//           {/* KPIs */}
//           <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
//             <KpiCard
//               label="Total Leads"
//               value={metrics.totalLeads}
//               sublabel={`${metrics.newLeads} new this period`}
//               filter="All"
//               leads={leads}                    // FULL LIST
//             />

//             <KpiCard
//               label="Won Deals"
//               value={metrics.wonLeads}
//               sublabel={formatCurrency(metrics.revenue) + " Won"}
//               filter="Won"
//               leads={leads}                    // FULL LIST
//             />

//             <KpiCard
//               label="Pipeline Value"
//               value={formatCurrency(metrics.pipeline)}
//               sublabel={`${metrics.inProgressLeads} in progress`}
//               filter="In Progress"
//               leads={leads}                    // FULL LIST
//             />

//             <KpiCard
//               label="Conversion Rate"
//               value={metrics.conversionRate + "%"}
//               sublabel={`${metrics.lostLeads} lost deals`}
//               filter="Lost"
//               leads={leads}                    // FULL LIST
//             />
//           </section>



//           {/* Leads + Create + Target */}
          
//         <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
//           <div className="md:col-span-2 xl:col-span-2 flex flex-col gap-4">
//             <LeadsTable
//               leads={filteredLeads}
//               filterStatus={filterStatus}
//               setFilterStatus={setFilterStatus}
//             />

//             <ReportsTable leads={leads} />
//           </div>


//           <div className="flex flex-col md:col-span-2 xl:col-span-1 gap-4">
//             <CreateLeadForm onCreate={handleCreateLead} />

//             <TargetCard
//               target={metrics.monthlyTarget}
//               achieved={metrics.revenue}
//             />

//             <IncentiveCard
//               targetIncentive={50000}
//               achievedIncentive={32000}
//             />
//           </div>

//         </section>

//         {/* Charts Section */}
//         <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">

//           {/* Monthly Won Revenue */}
//           <div className="bg-white rounded-2xl p-4 shadow-sm h-full flex flex-col">
//             <MonthlyWonChart leads={leads} />
//           </div>

//           {/* Leads Status */}
//           <div className="bg-white rounded-2xl p-4 shadow-sm h-full flex flex-col">
//             <LeadsByStatusBar leads={leads} />
//           </div>

//         </section>

//           {/* Team members section */}
//           {canViewTeamMembers && <TeamMembersSection leads={leads} />}
//         </div>
//       </main>

//       <AccessBlockedModal
//         user={authUser}
//         allowedRoles={[2]}
//         title="Account Details Required"
//         message="Account Details is mandatory to use this feature."
//         subMessage="Complete Zoho setup from Account → Zoho Details."
//       />
//     </div>
//   );
// }